import React, { useCallback, useEffect, useState } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants.ts";
import { isValidImageDimensions } from "@components/FileUpload/helper";
import SingleFile from "@components/FileUpload/SingleFile";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem.tsx";
import FormSection from "@components/FormSection/FormSection";
import { useNotification } from "@context/NotificationContext";
import type { FormContext } from "@features/port-security/components/SecurityClearence.types.ts";
import { useAzureBlobUpload } from "@features/port-security/hooks/useAzureBlobUpload";
import type { PortSecurityFormData } from "@features/port-security/schema/portSecurityFormSchema";
import { usePortSecurityFormStore } from "@features/port-security/store/usePortSecurityForm.store";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types.ts";
import { getSasToken } from "@services/userApi.ts";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

type UploadField =
  | "photograph"
  | "passportFront"
  | "passportBack"
  | "emiratesIdFront"
  | "emiratesIdBack"
  | "visa";
const DocumentType: Record<UploadField, string> = Object.freeze({
  photograph: "PHOTOGRAPH",
  passportFront: "PASSPORT_FRONT",
  passportBack: "PASSPORT_BACK",
  emiratesIdFront: "EMIRATES_ID_FRONT",
  emiratesIdBack: "EMIRATES_ID_BACK",
  visa: "VISA",
} as const);
const PHOTOGRAPH_MIN_PIXELS = 100;
const PHOTOGRAPH_MAX_PIXELS = 400;
type UploadItem = {
  name?: string;
  maxFiles?: number;
  url?: string;
  certificateType?: string;
  idx?: number;
};

const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];

interface UploadUpdate {
  name: UploadField | null;
  localValue: UploadItem[];
}

const VisitDetailsAndDocuments: React.FC = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const { formData, updateFormData } = useOutletContext<FormContext>();
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const imageAllowedExtensions = FILE_UPLOAD_PROFILES.images.format ?? [];
  const documentAllowedExtensions = Array.from(
    new Set([...(FILE_UPLOAD_PROFILES.images.format ?? []), "pdf"]),
  );

  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const updateFilesForKey = useUploadStore((state) => state.updateFilesForKey);
  const beginUploadInFlight = useUploadStore((state) => state.beginUploadInFlight);
  const endUploadInFlight = useUploadStore((state) => state.endUploadInFlight);
  const uploadsInFlightByKey = useUploadStore((state) => state.uploadsInFlightByKey);
  const isFieldUploading = useCallback(
    (key: UploadField) => (uploadsInFlightByKey[key] ?? 0) > 0,
    [uploadsInFlightByKey],
  );
  const photographFiles = useUploadStore(
    (state) => state.filesByKey.photograph ?? EMPTY_UPLOAD_ENTRIES,
  );
  const passportFrontFiles = useUploadStore(
    (state) => state.filesByKey.passportFront ?? EMPTY_UPLOAD_ENTRIES,
  );
  const passportBackFiles = useUploadStore(
    (state) => state.filesByKey.passportBack ?? EMPTY_UPLOAD_ENTRIES,
  );
  const emiratesIdFrontFiles = useUploadStore(
    (state) => state.filesByKey.emiratesIdFront ?? EMPTY_UPLOAD_ENTRIES,
  );
  const emiratesIdBackFiles = useUploadStore(
    (state) => state.filesByKey.emiratesIdBack ?? EMPTY_UPLOAD_ENTRIES,
  );
  const visaFiles = useUploadStore((state) => state.filesByKey.visa ?? EMPTY_UPLOAD_ENTRIES);

  const applyUploadFiles = useCallback(
    (key: UploadField, next: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) => {
      if (typeof next === "function") {
        updateFilesForKey(key, next as (prev: UploadEntry[]) => UploadEntry[]);
      } else {
        setFilesForKey(key, next);
      }
    },
    [setFilesForKey, updateFilesForKey],
  );

  const setPhotographFiles = useCallback(
    (next: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) =>
      applyUploadFiles("photograph", next),
    [applyUploadFiles],
  );
  const setPassportFrontFiles = useCallback(
    (next: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) =>
      applyUploadFiles("passportFront", next),
    [applyUploadFiles],
  );
  const setPassportBackFiles = useCallback(
    (next: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) =>
      applyUploadFiles("passportBack", next),
    [applyUploadFiles],
  );
  const setEmiratesIdFrontFiles = useCallback(
    (next: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) =>
      applyUploadFiles("emiratesIdFront", next),
    [applyUploadFiles],
  );
  const setEmiratesIdBackFiles = useCallback(
    (next: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) =>
      applyUploadFiles("emiratesIdBack", next),
    [applyUploadFiles],
  );
  const setVisaFiles = useCallback(
    (next: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) =>
      applyUploadFiles("visa", next),
    [applyUploadFiles],
  );

  useEffect(() => {
    const storeState = useUploadStore.getState();
    const portSecurityState = usePortSecurityFormStore.getState();
    const mappings: [UploadField, any][] = [
      ["photograph", portSecurityState.photograph],
      ["passportFront", portSecurityState.passportFront],
      ["passportBack", portSecurityState.passportBack],
      ["emiratesIdFront", portSecurityState.emiratesIdFront],
      ["emiratesIdBack", portSecurityState.emiratesIdBack],
      ["visa", portSecurityState.visa],
    ];

    mappings.forEach(([key, value]) => {
      if (Array.isArray(value) && value.length && storeState.getFilesForKey(key).length === 0) {
        storeState.setFilesForKey(key, value as UploadEntry[]);
      }
    });
  }, []);

  // const initialUloadUpdateValue: UploadUpdate = { name: "", localValue: [] };
  const [uploadUpdate, setUploadUpdate] = useState<UploadUpdate>({
    name: null,
    localValue: [],
  });

  const updateArray = (key: UploadField, item: { documentUrl: string; idx?: number }) => {
    const currentVal = formData.getValues(key as keyof PortSecurityFormData);
    const currentState: any[] = Array.isArray(currentVal) ? currentVal : [];

    const certificateType = DocumentType[key];
    if (!certificateType) {
      console.warn(`Unknown upload key: ${key}`);
      return;
    }
    const { documentUrl } = item;

    const idxFromResponse = Number.isInteger(item.idx) ? (item.idx as number) : -1;
    const fallbackIdx = currentState.findIndex(
      (it) => !it?.url && it?.certificateType === certificateType,
    );
    const targetIdx =
      idxFromResponse >= 0 ? idxFromResponse : fallbackIdx >= 0 ? fallbackIdx : currentState.length;

    const next = [...currentState];
    const prev = next[targetIdx] ?? {};
    next[targetIdx] = { name: prev?.name, url: documentUrl, certificateType, idx: targetIdx };
    formData.setValue(key as keyof PortSecurityFormData, next, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    applyUploadFiles(key, next as UploadEntry[]);
    setUploadUpdate({
      name: key as UploadField,
      localValue: next as UploadItem[],
    });
  };

  const handleUploadSuccess = (
    dataArray: { documentUrl: string; certificateType: string }[],
    context: { certificateType: string; storeKey: string },
  ) => {
    if (!Array.isArray(dataArray)) return;
    const { storeKey } = context;
    const uploadField = storeKey as UploadField;
    if (!DocumentType[uploadField]) {
      console.warn(`Unknown store key: ${storeKey}`);
      return;
    }

    dataArray.forEach((data, idx) => {
      updateArray(uploadField, { documentUrl: data.documentUrl, idx });
    });
  };

  const handleUploadError = (error: Error) => {
    notify({
      title: t("security-clearance.notifications.sc-request-upload-error-title"),
      description: t("security-clearance.notifications.sc-request-upload-error-description"),
      type: "error",
    });
    console.error("Error on upload documents:", error);
  };

  const uploadDocumentsToAzure = useAzureBlobUpload({
    handleUploadSuccess,
    handleUploadError,
    transactionContext: {
      module: "security_clearance",
      screen: "SecurityClearanceRequest",
      action: "upload_security_clearance_document",
      entityType: "security_clearance_document",
    },
  });

  const validatePhotographDimensions = useCallback(
    async (files: File[]) => {
      if (!Array.isArray(files) || files.length === 0) return null;
      const results = await Promise.all(
        files.map((file) =>
          isValidImageDimensions(file, PHOTOGRAPH_MIN_PIXELS, PHOTOGRAPH_MAX_PIXELS),
        ),
      );
      const hasInvalid = results.some((isValid) => !isValid);
      if (hasInvalid) {
        return t("upload.picture-size", {
          min: PHOTOGRAPH_MIN_PIXELS,
          max: PHOTOGRAPH_MAX_PIXELS,
        });
      }
      return null;
    },
    [t],
  );

  const buildFormItemsFromStore = useCallback(
    (key: UploadField): { name: string; certificateType: string; url?: string; idx: number }[] => {
      const certificateType = DocumentType[key];
      if (!certificateType) return [];
      const storeEntries = useUploadStore.getState().getFilesForKey(key) ?? EMPTY_UPLOAD_ENTRIES;
      return storeEntries.map((entry, idx) => {
        if (entry instanceof File) {
          return {
            name: entry.name,
            certificateType,
            url: undefined,
            idx,
          };
        }
        const typedEntry = entry as UploadItem;
        return {
          name: typedEntry.name ?? "",
          certificateType: typedEntry.certificateType ?? certificateType,
          url: typedEntry.url,
          idx: typeof typedEntry.idx === "number" ? typedEntry.idx : idx,
        };
      });
    },
    [],
  );

  const removePendingFile = useCallback(
    (uploadField: UploadField, fileToRemove: File) => {
      applyUploadFiles(uploadField, (prev) =>
        prev.filter((entry) => !(entry instanceof File && entry === fileToRemove)),
      );
      const updatedEntries = buildFormItemsFromStore(uploadField);
      formData.setValue(uploadField as keyof PortSecurityFormData, updatedEntries, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setUploadUpdate({
        name: uploadField,
        localValue: updatedEntries as UploadItem[],
      });
    },
    [applyUploadFiles, buildFormItemsFromStore, formData, setUploadUpdate],
  );

  const handleFieldChangeForAzure = async (fieldName: string, files: File[]) => {
    const uploadField = fieldName as UploadField;
    const certificateType = DocumentType[uploadField];
    if (!certificateType) {
      console.warn(`Unknown field: ${fieldName}`);
      return;
    }

    const incomingFiles = Array.isArray(files)
      ? files.filter((entry): entry is File => entry instanceof File)
      : [];
    const formEntries = buildFormItemsFromStore(uploadField);

    formData.setValue(uploadField as keyof PortSecurityFormData, formEntries, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    if (incomingFiles.length === 0) return;

    await Promise.all(
      incomingFiles.map(async (file, idx) => {
        beginUploadInFlight(uploadField);
        try {
          const payloadForSasTokenActionWrite = {
            prefix: SasTokenPrefix.GATEPASS_SECURITY_CLEARANCE,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          let sasTokenResponse: Awaited<ReturnType<typeof getSasToken>>;
          try {
            sasTokenResponse = await getSasToken(payloadForSasTokenActionWrite);
          } catch (error) {
            const normalizedError =
              error instanceof Error ? error : new Error("Failed to obtain SAS token from API.");
            handleUploadError(normalizedError);
            removePendingFile(uploadField, file);
            return;
          }

          try {
            await uploadDocumentsToAzure.mutateAsync({
              file,
              blobUrl: sasTokenResponse.blobUrl,
              sasToken: sasTokenResponse.sasToken,
              certificateType,
              storeKey: fieldName,
              idx,
            });
          } catch (error) {
            const normalizedError =
              error instanceof Error ? error : new Error("Failed to upload document to Azure.");
            handleUploadError(normalizedError);
            removePendingFile(uploadField, file);
          }
        } finally {
          endUploadInFlight(uploadField);
        }
      }),
    );
  };

  const handleRemoveFile = (key: string, index: number) => {
    const currentState = formData.getValues(key as keyof PortSecurityFormData);
    const updatedState = currentState.filter((_: File, i: number) => i !== index);

    formData.setValue(key as keyof PortSecurityFormData, updatedState, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    applyUploadFiles(key as UploadField, updatedState as UploadEntry[]);
    setUploadUpdate({
      name: key as UploadField,
      localValue: updatedState as UploadItem[],
    });
  };

  // We need this in order to trigger an update on the entire local storage & store only one time per upload/delete
  useEffect(() => {
    if (uploadUpdate.name !== null) {
      updateFormData(uploadUpdate.name, uploadUpdate.localValue);
      setUploadUpdate({ name: null, localValue: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadUpdate.name, uploadUpdate.localValue]);

  return (
    <FormProvider {...formData}>
      <FormSection title={t("security-clearance.upload-your-photograph")}>
        <div className="col-span-full">
          <FormItem<PortSecurityFormData, "photograph">
            name="photograph"
            type="file"
            testId="visitDetailsAndDocuments-fileUpload-photograph"
            uploadConditions={FILE_UPLOAD_PROFILES.files}
            maxFiles={1}
            allowedFileExtensions={imageAllowedExtensions}
            onUpload={handleFieldChangeForAzure}
            uploadDescription={t("upload.photograph")}
            files={photographFiles}
            setFiles={setPhotographFiles}
            fileValidation={validatePhotographDimensions}
            disabled={isFieldUploading("photograph")}
          />
        </div>
        {photographFiles.length
          ? photographFiles.map((file, idx) => (
              <div className="flex-1" key={`${file.name}-${(file as File).lastModified ?? idx}`}>
                <SingleFile
                  testId={`photograph-${idx}`}
                  file={file}
                  currentFileIndex={idx}
                  fieldName="photograph"
                  isLoading={isFieldUploading("photograph")}
                  setFiles={() => {
                    setPhotographFiles((prev) => prev.filter((_, i) => i !== idx));
                    handleRemoveFile("photograph", idx);
                  }}
                />
              </div>
            ))
          : null}
      </FormSection>

      <FormSection title={t("security-clearance.upload-passport-front-and-back")}>
        <div className="col-span-full grid gap-4 md:grid-cols-2">
          <div className="flex gap-4 w-full flex-wrap">
            <FormItem<PortSecurityFormData, "passportFront">
              name="passportFront"
              label={t("common.front")}
              type="file"
              testId="visitDetailsAndDocuments-fileUpload-passport-front"
              uploadConditions={FILE_UPLOAD_PROFILES.files}
              maxFiles={1}
              allowedFileExtensions={documentAllowedExtensions}
              uploadDescription={t("upload.drag-or-drop-description-10mb")}
              onUpload={handleFieldChangeForAzure}
              files={passportFrontFiles}
              setFiles={setPassportFrontFiles}
              disabled={isFieldUploading("passportFront")}
            />
            {passportFrontFiles[0] && (
              <div className="flex-1">
                <SingleFile
                  testId="passport-front"
                  file={passportFrontFiles[0]}
                  currentFileIndex={0}
                  fieldName="passportFront"
                  isLoading={isFieldUploading("passportFront")}
                  setFiles={() => {
                    setPassportFrontFiles([]);
                    handleRemoveFile("passportFront", 0);
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex gap-4 w-full flex-wrap">
            <FormItem<PortSecurityFormData, "passportBack">
              name="passportBack"
              label={t("common.back")}
              type="file"
              testId="visitDetailsAndDocuments-fileUpload-passport-back"
              uploadConditions={FILE_UPLOAD_PROFILES.files}
              maxFiles={1}
              allowedFileExtensions={documentAllowedExtensions}
              uploadDescription={t("upload.drag-or-drop-description-10mb")}
              onUpload={handleFieldChangeForAzure}
              files={passportBackFiles}
              setFiles={setPassportBackFiles}
              disabled={isFieldUploading("passportBack")}
            />
            {passportBackFiles[0] && (
              <div className="flex-1">
                <SingleFile
                  testId="passport-back"
                  file={passportBackFiles[0]}
                  currentFileIndex={0}
                  fieldName="passportBack"
                  isLoading={isFieldUploading("passportBack")}
                  setFiles={() => {
                    setPassportBackFiles([]);
                    handleRemoveFile("passportBack", 0);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </FormSection>
      <FormSection title={t("security-clearance.upload-emirates-id-front-and-back")}>
        <div className="col-span-full grid gap-4 md:grid-cols-2">
          <div className="flex gap-4 w-full flex-wrap">
            <FormItem<PortSecurityFormData, "emiratesIdFront">
              name="emiratesIdFront"
              label={t("common.front")}
              type="file"
              testId="visitDetailsAndDocuments-fileUpload-emirates-id-front"
              uploadConditions={FILE_UPLOAD_PROFILES.files}
              maxFiles={1}
              allowedFileExtensions={documentAllowedExtensions}
              uploadDescription={t("upload.drag-or-drop-description-10mb")}
              onUpload={handleFieldChangeForAzure}
              files={emiratesIdFrontFiles}
              setFiles={setEmiratesIdFrontFiles}
              disabled={isFieldUploading("emiratesIdFront")}
            />
            {emiratesIdFrontFiles[0] && (
              <div className="flex-1">
                <SingleFile
                  testId="emirates-id-front"
                  file={emiratesIdFrontFiles[0]}
                  currentFileIndex={0}
                  fieldName="emiratesIdFront"
                  isLoading={isFieldUploading("emiratesIdFront")}
                  setFiles={() => {
                    setEmiratesIdFrontFiles([]);
                    handleRemoveFile("emiratesIdFront", 0);
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex gap-4 w-full flex-wrap">
            <FormItem<PortSecurityFormData, "emiratesIdBack">
              name="emiratesIdBack"
              label={t("common.back")}
              type="file"
              testId="visitDetailsAndDocuments-fileUpload-emirates-id-back"
              uploadConditions={FILE_UPLOAD_PROFILES.files}
              maxFiles={1}
              allowedFileExtensions={documentAllowedExtensions}
              uploadDescription={t("upload.drag-or-drop-description-10mb")}
              onUpload={handleFieldChangeForAzure}
              files={emiratesIdBackFiles}
              setFiles={setEmiratesIdBackFiles}
              disabled={isFieldUploading("emiratesIdBack")}
            />
            {emiratesIdBackFiles[0] && (
              <div className="flex-1">
                <SingleFile
                  testId="emirates-id-back"
                  file={emiratesIdBackFiles[0]}
                  currentFileIndex={0}
                  fieldName="emiratesIdBack"
                  isLoading={isFieldUploading("emiratesIdBack")}
                  setFiles={() => {
                    setEmiratesIdBackFiles([]);
                    handleRemoveFile("emiratesIdBack", 0);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </FormSection>

      <FormSection title={t("security-clearance.upload-your-visa")}>
        <div className="col-span-full">
          <FormItem<PortSecurityFormData, "visa">
            name="visa"
            type="file"
            testId="visitDetailsAndDocuments-fileUpload-visa"
            uploadConditions={FILE_UPLOAD_PROFILES.files}
            maxFiles={1}
            allowedFileExtensions={documentAllowedExtensions}
            uploadDescription={t("upload.drag-or-drop-description-10mb")}
            onUpload={handleFieldChangeForAzure}
            files={visaFiles}
            setFiles={setVisaFiles}
            disabled={isFieldUploading("visa")}
          />
        </div>
        <div className="flex gap-4 w-full flex-wrap">
          {visaFiles.map((file, idx) => (
            <div className="flex-1" key={`${file.name}-${idx}`}>
              <SingleFile
                testId={`visa-${idx}`}
                file={file}
                currentFileIndex={idx}
                fieldName="visa"
                isLoading={isFieldUploading("visa")}
                setFiles={() => {
                  setVisaFiles((prev) => prev.filter((_, fileIdx) => fileIdx !== idx));
                  handleRemoveFile("visa", idx);
                }}
              />
            </div>
          ))}
        </div>
      </FormSection>
    </FormProvider>
  );
};

export default VisitDetailsAndDocuments;
